import { GroupInvitationStatus } from 'multirpc-sdk';
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { guardDialogSlice } from 'modules/guardDialog';
import { useAccount } from 'modules/account/hooks/useAccount';
import { useAccountStatus } from 'modules/accountStatus/hooks/useAccountStatus';
import { useAuth } from 'modules/auth/hooks/useAuth';
import { useCommonNotificationsData } from 'modules/notifications/hooks/useCommonNotificationsData';
import { usePartnerClientStatus } from 'domains/jwtToken/hooks/usePartnerClientStatus';
import { useTeamInvitationsQuery } from 'modules/teams/actions/hooks/useTeamInvitationsQuery';
import { useUpgradePlanDialog } from 'modules/common/components/UpgradePlanDialog';
import { useIsXSDown } from 'uiKit/Theme/useTheme';

import { getSecondMenuItems } from '../utils/getSecondMenuItems';
import { getTopMenuItems } from '../utils/getTopMenuItems';

export interface IUseMenuItemsProps {
  chainsRoutes: string[];
  isMobileSideBar: boolean;
}

const invitationsStatuses = [GroupInvitationStatus.PENDING];

export const useMenuItems = ({
  chainsRoutes,
  isMobileSideBar,
}: IUseMenuItemsProps) => {
  const { isPersonalAccount } = useAccount();
  const { isEnterprise } = useAccountStatus();
  const { isLoggedIn, isWeb3Auth } = useAuth();
  const dispatch = useDispatch();

  const { isPartnerClient } = usePartnerClientStatus();

  const {
    isOpened: isUpgradePlanDialogOpened,
    onClose: handleUpgradePlanDialogClose,
    onOpen: onOpenUpgradePlanDialog,
  } = useUpgradePlanDialog();

  const onOpenAccessDeniedDialog = useCallback(() => {
    dispatch(guardDialogSlice.actions.showDialog());
  }, [dispatch]);

  const { unseenNotificationsAmount } = useCommonNotificationsData();
  const { teamInvitationsAmount } = useTeamInvitationsQuery({
    invitationsStatuses,
    skipFetching: true,
  });

  const isXsDown = useIsXSDown();

  const topMenuItems = useMemo(
    () =>
      getTopMenuItems({
        chainsRoutes,
        isEnterpriseClient: isEnterprise,
        isLoggedIn,
        isMobileSideBar,
        isXsDown,
        isPartnerClient,
        isPersonalGroup: isPersonalAccount,
        isWeb3Auth,
        notificationsAmount: unseenNotificationsAmount,
        onOpenAccessDeniedDialog,
        onOpenUpgradePlanDialog,
        teamInvitationsAmount,
      }),
    [
      chainsRoutes,
      isEnterprise,
      isLoggedIn,
      isMobileSideBar,
      isPartnerClient,
      isPersonalAccount,
      isWeb3Auth,
      isXsDown,
      onOpenAccessDeniedDialog,
      onOpenUpgradePlanDialog,
      teamInvitationsAmount,
      unseenNotificationsAmount,
    ],
  );

  const secondMenuItems = useMemo(
    () =>
      getSecondMenuItems({
        isEnterpriseClient: isEnterprise,
        isLoggedIn,
        isPartnerClient,
      }),
    [isEnterprise, isLoggedIn, isPartnerClient],
  );

  return {
    handleUpgradePlanDialogClose,
    isUpgradePlanDialogOpened,
    secondMenuItems,
    topMenuItems,
  };
};
